import React from 'react'
import { matchPath, useLocation } from 'react-router'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { radius } from '@mobi/component-library/Foundations'
import { AppRoutes } from '@core/AppRoutes'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'
import { useRadio } from '@core/Areas/Navigation/Components/TabNavigation/hooks/useRadio'
import { selectIsSkyVideoPlayerOpen } from '@core/Areas/SkyVideoPlayer/Store/selectors'
import { useAppSelector } from '@core/Store/hooks'
import { selectIsRadioPlaying } from '../../Store/selectors'
import { TabRadioIcon } from './Components/TabRadioIcon'
import { trackOnRadioToggle } from './helpers/analytics'

export const FloatingTabRadioButton: React.FC = () => {
  const isRadioPlayingSelector = useAppSelector<boolean>(selectIsRadioPlaying)
  const isSkyVideoPlayerOpen = useAppSelector(selectIsSkyVideoPlayerOpen)

  const { isRadioPlaying, isRadioBusy, handleRadioToggle } = useRadio(isSkyVideoPlayerOpen)

  const toggleRadio = () => {
    handleRadioToggle()
    trackOnRadioToggle(
      isRadioPlayingSelector,
      'floating-nav',
      `${location.pathname}${location.search}`
    )
  }

  const location = useLocation()
  const isVisible = matchPath(AppRoutes.Home + '/*', location.pathname)

  return (
    <FloatingTabRadioButtonContainerStyled
      id='floatingRadioPlayer'
      style={{ display: isVisible ? undefined : 'none' }}
    >
      <button
        role='button'
        type='button'
        name='tab-radio-button'
        aria-label='TAB Radio Button'
        onClick={toggleRadio}
      >
        <TabRadioIcon />
      </button>

      {(isRadioPlaying || isRadioBusy) && (
        <div data-testid='tab-radio-playing'>
          <Icon
            name='SolidPlayCircle'
            size='1.7rem'
            color={isRadioBusy ? 'transparent' : 'white'}
          />
        </div>
      )}
    </FloatingTabRadioButtonContainerStyled>
  )
}

// ======
// Styles
// ======

const FloatingTabRadioButtonContainerStyled = styled.div({
  width: '5rem',
  height: '5rem',
  background: 'linear-gradient(180deg, #8246AF 24.48%, #6A3A8F 48.96%, #512D6D 74.48%);',
  borderRadius: '1rem',
  zIndex: '3',
  position: 'fixed',
  display: 'flex',
  right: 'calc(max(1rem, 50vw - 40rem + 1rem))',
  filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  alignItems: 'center',
  bottom: shouldApplySafeAreaBottomPadding
    ? 'calc(7rem + var(--safe-area-inset-bottom, 0px))'
    : '2rem',

  button: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: 'none',
    backgroundColor: 'transparent',
    padding: 0,
    color: hexColors.white,
    width: '100%',
    fontSize: '3em',
    cursor: 'pointer',
  },

  '> div': {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: '0.2rem',
    top: '0.2rem',
    padding: '0.5px',
    color: hexColors.white,
    background: '#9E56FF',
    borderRadius: radius.full,
    pointerEvents: 'none',
  },
})
